import type {
  DesktopPraxaAutomationsBridge,
  DesktopPraxaAutomationRunsBridge,
  PraxaDesktopAutomationCatalogState,
  PraxaDesktopAutomationDetail,
  PraxaDesktopAutomationManagementDetail,
  PraxaDesktopAutomationManagementInput,
  PraxaDesktopAutomationManagementResult,
  PraxaDesktopAutomationRunControlAction,
  PraxaDesktopAutomationRunState,
} from "@praxa/platform-contracts";
import { useCallback, useEffect, useRef, useState } from "react";

export const PRAXA_AUTOMATIONS_LOAD_ERROR =
  "Praxa automations couldn’t load. Local coding is still available.";
export const PRAXA_AUTOMATION_DETAIL_LOAD_ERROR =
  "This automation couldn’t load. Try again when the cloud connection is available.";
export const PRAXA_AUTOMATION_MANAGEMENT_LOAD_ERROR =
  "Automation settings couldn’t load. Try again when Praxa is online.";

function getBridge(): DesktopPraxaAutomationsBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaAutomations;
}

function getRunBridge(): DesktopPraxaAutomationRunsBridge | undefined {
  return typeof window === "undefined" ? undefined : window.desktopBridge?.praxaAutomationRuns;
}

export function isPraxaAutomationSelectionAuthorized(
  state: PraxaDesktopAutomationCatalogState | null,
  workspaceId: string,
  automationId: string,
): boolean {
  return (
    state !== null &&
    state.workspaceId === workspaceId &&
    state.automations.some(
      (automation) =>
        automation.workspaceId === workspaceId && automation.automationId === automationId,
    )
  );
}

export function praxaAutomationDetailRequestKey(
  workspaceId: string,
  automationId: string,
  authorityRevision: number,
  retryRequest: number,
): string {
  return `${authorityRevision}:${retryRequest}:${workspaceId}:${automationId}`;
}

export function useDesktopPraxaAutomations(): {
  readonly bridge: DesktopPraxaAutomationsBridge | undefined;
  readonly error: string | null;
  readonly loading: boolean;
  readonly refresh: () => Promise<void>;
  readonly revision: number;
  readonly state: PraxaDesktopAutomationCatalogState | null;
} {
  const bridge = getBridge();
  const [state, setState] = useState<PraxaDesktopAutomationCatalogState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const eventVersionRef = useRef(0);

  const applyState = useCallback((nextState: PraxaDesktopAutomationCatalogState) => {
    setState(nextState);
    setError(null);
    setRevision((current) => current + 1);
  }, []);

  const refresh = useCallback(async () => {
    if (!bridge) return;
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    setError(null);
    try {
      const nextState = await bridge.refresh();
      if (eventVersionRef.current === eventVersion) applyState(nextState);
    } catch {
      if (eventVersionRef.current === eventVersion) {
        setError(PRAXA_AUTOMATIONS_LOAD_ERROR);
      }
    } finally {
      setLoading(false);
    }
  }, [applyState, bridge]);

  useEffect(() => {
    if (!bridge) {
      setState(null);
      setError(null);
      setLoading(false);
      return;
    }
    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onStateChange((nextState) => {
        eventVersionRef.current += 1;
        if (active) applyState(nextState);
      });
    } catch {
      setError(PRAXA_AUTOMATIONS_LOAD_ERROR);
    }
    const eventVersion = eventVersionRef.current;
    setLoading(true);
    void bridge
      .getState()
      .then((nextState) => {
        if (active && eventVersionRef.current === eventVersion) {
          applyState(nextState);
        }
      })
      .catch(() => {
        if (active && eventVersionRef.current === eventVersion) {
          setError(PRAXA_AUTOMATIONS_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [applyState, bridge]);

  return { bridge, error, loading, refresh, revision, state };
}

interface AutomationDetailRequestState {
  readonly requestKey: string | null;
  readonly detail: PraxaDesktopAutomationDetail | null;
  readonly error: string | null;
  readonly loading: boolean;
}

export function useDesktopPraxaAutomationDetail(input: {
  readonly workspaceId: string;
  readonly automationId: string;
  readonly authorized: boolean;
  readonly authorityRevision: number;
}): {
  readonly detail: PraxaDesktopAutomationDetail | null;
  readonly error: string | null;
  readonly loading: boolean;
  readonly retry: () => void;
} {
  const bridge = getBridge();
  const [retryRequest, setRetryRequest] = useState(0);
  const [requestState, setRequestState] = useState<AutomationDetailRequestState>({
    requestKey: null,
    detail: null,
    error: null,
    loading: false,
  });
  const requestVersionRef = useRef(0);
  const requestKey = praxaAutomationDetailRequestKey(
    input.workspaceId,
    input.automationId,
    input.authorityRevision,
    retryRequest,
  );

  useEffect(() => {
    const requestVersion = ++requestVersionRef.current;
    if (!bridge || !input.authorized) {
      setRequestState({
        requestKey,
        detail: null,
        error: bridge ? null : PRAXA_AUTOMATION_DETAIL_LOAD_ERROR,
        loading: false,
      });
      return;
    }
    setRequestState({
      requestKey,
      detail: null,
      error: null,
      loading: true,
    });
    void bridge
      .loadAutomationDetail(input.workspaceId, input.automationId)
      .then((detail) => {
        if (requestVersionRef.current !== requestVersion) return;
        setRequestState({
          requestKey,
          detail,
          error: detail === null ? PRAXA_AUTOMATION_DETAIL_LOAD_ERROR : null,
          loading: false,
        });
      })
      .catch(() => {
        if (requestVersionRef.current === requestVersion) {
          setRequestState({
            requestKey,
            detail: null,
            error: PRAXA_AUTOMATION_DETAIL_LOAD_ERROR,
            loading: false,
          });
        }
      });
    return () => {
      requestVersionRef.current += 1;
    };
  }, [
    bridge,
    input.authorityRevision,
    input.authorized,
    input.automationId,
    input.workspaceId,
    requestKey,
  ]);

  if (requestState.requestKey !== requestKey) {
    return {
      detail: null,
      error: null,
      loading: input.authorized,
      retry: () => {},
    };
  }
  return {
    detail: requestState.detail,
    error: requestState.error,
    loading: requestState.loading,
    retry: () => setRetryRequest((request) => request + 1),
  };
}

export function useDesktopPraxaAutomationManagementDetail(input: {
  readonly workspaceId: string;
  readonly automationId: string;
  readonly authorized: boolean;
  readonly authorityRevision: number;
}): {
  readonly detail: PraxaDesktopAutomationManagementDetail | null;
  readonly error: string | null;
  readonly loading: boolean;
  readonly retry: () => void;
} {
  const bridge = getBridge();
  const [retryRequest, setRetryRequest] = useState(0);
  const [detail, setDetail] = useState<PraxaDesktopAutomationManagementDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!bridge || !input.authorized) {
      setDetail(null);
      setError(bridge ? null : PRAXA_AUTOMATION_MANAGEMENT_LOAD_ERROR);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    void bridge
      .loadManagementDetail(input.workspaceId, input.automationId)
      .then((nextDetail) => {
        if (!active) return;
        setDetail(nextDetail);
        setError(nextDetail === null ? PRAXA_AUTOMATION_MANAGEMENT_LOAD_ERROR : null);
      })
      .catch(() => {
        if (active) {
          setDetail(null);
          setError(PRAXA_AUTOMATION_MANAGEMENT_LOAD_ERROR);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    bridge,
    input.authorityRevision,
    input.authorized,
    input.automationId,
    input.workspaceId,
    retryRequest,
  ]);

  return {
    detail,
    error,
    loading,
    retry: () => setRetryRequest((request) => request + 1),
  };
}

export function useDesktopPraxaAutomationMutation(): {
  readonly acting: boolean;
  readonly error: string | null;
  readonly mutate: (
    input: PraxaDesktopAutomationManagementInput,
  ) => Promise<PraxaDesktopAutomationManagementResult | null>;
} {
  const bridge = getBridge();
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(
    async (
      input: PraxaDesktopAutomationManagementInput,
    ): Promise<PraxaDesktopAutomationManagementResult | null> => {
      if (!bridge || acting) return null;
      setActing(true);
      setError(null);
      try {
        const result = await bridge.mutate(input);
        if (result.status === "error") setError(result.message);
        return result;
      } catch {
        setError("Praxa could not complete this automation change.");
        return null;
      } finally {
        setActing(false);
      }
    },
    [acting, bridge],
  );

  return { acting, error, mutate };
}

export function useDesktopPraxaAutomationRun(): {
  readonly bridge: DesktopPraxaAutomationRunsBridge | undefined;
  readonly error: string | null;
  readonly state: PraxaDesktopAutomationRunState | null;
  readonly run: (
    workspaceId: string,
    automationId: string,
  ) => Promise<PraxaDesktopAutomationRunState | null>;
  readonly control: (
    requestId: string,
    action: PraxaDesktopAutomationRunControlAction,
  ) => Promise<PraxaDesktopAutomationRunState | null>;
  readonly retry: (requestId: string) => Promise<PraxaDesktopAutomationRunState | null>;
  readonly discard: (requestId: string) => Promise<PraxaDesktopAutomationRunState | null>;
} {
  const bridge = getRunBridge();
  const [state, setState] = useState<PraxaDesktopAutomationRunState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!bridge) {
      setState(null);
      setError(null);
      return;
    }
    let active = true;
    let unsubscribe = () => {};
    try {
      unsubscribe = bridge.onStateChange((next) => {
        if (active) {
          setState(next);
          setError(null);
        }
      });
    } catch {
      setError("The protected automation run bridge is unavailable.");
    }
    void bridge
      .getState()
      .then((next) => {
        if (active) setState(next);
      })
      .catch(() => {
        if (active) {
          setError("The protected automation run bridge is unavailable.");
        }
      });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [bridge]);

  const invoke = useCallback(
    async (
      operation: () => Promise<PraxaDesktopAutomationRunState>,
    ): Promise<PraxaDesktopAutomationRunState | null> => {
      if (!bridge) return null;
      setError(null);
      try {
        const next = await operation();
        setState(next);
        return next;
      } catch {
        setError("Praxa could not update this automation run.");
        return null;
      }
    },
    [bridge],
  );

  return {
    bridge,
    control: (requestId, action) => invoke(() => bridge!.control(requestId, action)),
    error,
    state,
    run: (workspaceId, automationId) => invoke(() => bridge!.run(workspaceId, automationId)),
    retry: (requestId) => invoke(() => bridge!.retry(requestId)),
    discard: (requestId) => invoke(() => bridge!.discard(requestId)),
  };
}
